import { useEffect, useState } from 'react';
import { useAppContext } from '@/App';
import { getMyFinances, getMyTuitionPayments } from '@/lib/api';
import { formatDateValue } from '@/lib/dates';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Banknote,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  Landmark,
  Lock,
  ReceiptText,
  WalletCards,
} from 'lucide-react';

const money = (value) => {
  const numeric = Number(value) || 0;
  return `$${numeric.toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const dateLabel = (value) => {
  return formatDateValue(value, {
    fallback: 'Not on file',
    locale: 'en-CA',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const statusLabel = (value) => {
  if (!value) return 'Not required';
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const statusTone = (status) => {
  if (status === 'paid' || status === 'waived') {
    return 'border-green-200 bg-green-50 text-green-700 hover:bg-green-50';
  }
  if (status === 'overdue') {
    return 'border-red-200 bg-red-50 text-red-700 hover:bg-red-50';
  }
  return 'border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#062B1A] hover:bg-[#D4AF37]/10';
};

function AmountTile({ label, value, icon: Icon, accent = false }) {
  return (
    <Card className={`card-hover border border-[rgba(6,43,26,0.08)] shadow-sm ${accent ? 'gold-border-left' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">{label}</span>
          <Icon className="w-4 h-4 text-[#D4AF37]" />
        </div>
        <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          {money(value)}
        </p>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-[#062B1A]/8 last:border-b-0">
      <span className="text-sm text-[#062B1A]/55">{label}</span>
      <span className="text-sm font-medium text-[#062B1A] text-right">{value}</span>
    </div>
  );
}

export default function Finances() {
  const { currentStudent, hydrating } = useAppContext();
  const [finances, setFinances] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadFinances = async () => {
      try {
        setLoading(true);
        setError('');
        const [financesRes, ledgerRes] = await Promise.all([
          getMyFinances(),
          // The ledger is supplementary; a failure here must not blank the page.
          getMyTuitionPayments().catch(() => null),
        ]);
        if (!cancelled) {
          setFinances(financesRes.data);
          setPayments(ledgerRes?.data?.items || []);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError('Finances could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadFinances();

    return () => {
      cancelled = true;
    };
  }, []);

  const tuitionTotal = Number(finances?.tuition_total) || 0;
  const tuitionPaid = Number(finances?.tuition_paid) || 0;
  const balance = Number(finances?.balance) || 0;
  const hasTuition = tuitionTotal > 0;
  const paidPercent = hasTuition ? Math.min(Math.max((tuitionPaid / tuitionTotal) * 100, 0), 100) : 0;

  if (loading || hydrating) {
    return (
      <div className="space-y-6">
        <div className="h-24 bg-white rounded-xl animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((item) => <div key={item} className="h-28 bg-white rounded-xl animate-pulse" />)}
        </div>
        <div className="h-72 bg-white rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!currentStudent && !finances?.student_id) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="text-center py-16">
          <WalletCards className="w-12 h-12 text-[#062B1A]/15 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Profile not provisioned yet
          </h2>
          <p className="text-sm text-[#062B1A]/50 mt-2 max-w-md mx-auto">
            Your student record has not been linked yet. Contact your administrator if this persists.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Finances
          </h2>
          <p className="text-sm text-[#062B1A]/60 mt-1">Tuition, deposit, loan, and fee status</p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-lg border border-[#062B1A]/10 bg-white px-3 py-2 text-sm text-[#062B1A]/60">
          <Lock className="w-4 h-4 text-[#D4AF37]" />
          Read-only student view
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Tuition Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasTuition ? (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <AmountTile label="Total" value={tuitionTotal} icon={ReceiptText} />
                <AmountTile label="Paid" value={tuitionPaid} icon={CheckCircle2} accent />
                <AmountTile label="Balance" value={balance} icon={Banknote} accent={balance > 0} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">Paid Progress</span>
                  <span className="text-sm font-medium text-[#062B1A]">{Math.round(paidPercent)}%</span>
                </div>
                <div
                  className="h-3 w-full overflow-hidden rounded-full bg-[#062B1A]/10"
                  role="progressbar"
                  aria-valuenow={Math.round(paidPercent)}
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-label="Tuition paid progress"
                >
                  <div
                    className="h-full rounded-full bg-[#D4AF37] transition-all"
                    style={{ width: `${paidPercent}%` }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <ReceiptText className="w-10 h-10 text-[#062B1A]/20 mx-auto mb-2" />
              <p className="text-sm font-medium text-[#062B1A]">No tuition on file yet</p>
              <p className="text-sm text-[#062B1A]/50 mt-1">
                Your tuition summary will appear here once your student record is updated.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {finances?.deposit_required && (
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                <CalendarDays className="w-4 h-4 text-[#D4AF37]" />
                Deposit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-3">
                <Badge variant="outline" className={statusTone(finances.deposit_status)}>
                  {statusLabel(finances.deposit_status)}
                </Badge>
              </div>
              <DetailRow label="Amount" value={money(finances.deposit_amount)} />
              <DetailRow label="Due date" value={dateLabel(finances.deposit_due_date)} />
              <DetailRow label="Paid date" value={dateLabel(finances.deposit_paid_date)} />
            </CardContent>
          </Card>
        )}

        {finances?.has_student_loan && (
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                <Landmark className="w-4 h-4 text-[#D4AF37]" />
                Student Loan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DetailRow label="Loan total" value={money(finances.loan_total)} />
              <DetailRow label="Loan paid" value={money(finances.loan_paid)} />
              <DetailRow label="Remaining" value={money(finances.loan_balance)} />
            </CardContent>
          </Card>
        )}

        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm md:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              <ReceiptText className="w-4 h-4 text-[#D4AF37]" />
              Tuition Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {payments.length === 0 ? (
              <p className="text-sm text-[#062B1A]/55">No tuition payments recorded yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-[rgba(6,43,26,0.08)]">
                <table className="w-full min-w-[420px] text-left text-sm">
                  <thead className="bg-[#F7F8FA] text-xs uppercase tracking-[0.08em] text-[#062B1A]/50">
                    <tr>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Method</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(6,43,26,0.06)]">
                    {payments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="px-3 py-2">{dateLabel(payment.payment_date)}</td>
                        <td className="px-3 py-2">{payment.method || '-'}</td>
                        <td className="px-3 py-2 text-right font-semibold">{money(payment.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              <CreditCard className="w-4 h-4 text-[#D4AF37]" />
              Application Fee
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Badge
              variant="outline"
              className={
                finances?.application_fee_paid
                  ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-50'
                  : 'border-[#062B1A]/15 bg-white text-[#062B1A]/60 hover:bg-white'
              }
            >
              {finances?.application_fee_paid ? 'Paid' : 'Not paid'}
            </Badge>
          </CardContent>
        </Card>

        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              <WalletCards className="w-4 h-4 text-[#D4AF37]" />
              Payment Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#062B1A]/55">Payment plan / installments - coming soon</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
